import detectTrackingType from "./detectTrackingType";
import {
  findScacNumberInList,
  isValidScacCode,
  resolveAutoContainerScacNumber,
  SCAC_OTHER_VALUE,
  ScacCode,
} from "./trackingScacUtils";

const CONTAINER_PATTERN = /^[A-Za-z]{4}\d{7}$/i;
const MBOL_REMAINDER_PATTERN = /^[0-9]{7,10}$/;

export type ResolvedPastedReference = {
  reference: string;
  selectedType: string;
  typeManuallySet: boolean;
  selectedScacNumber: string | null;
  scacOtherCode: string;
};

function resolveMbolPasteSplit(
  trimmed: string,
  scacCodes: ScacCode[],
  typeManuallySet: boolean,
): ResolvedPastedReference | null {
  if (trimmed.length < 4) return null;

  const firstFour = trimmed.slice(0, 4).toUpperCase();
  const remainder = trimmed.slice(4);

  if (!isValidScacCode(firstFour) || !MBOL_REMAINDER_PATTERN.test(remainder)) {
    return null;
  }

  const pastedScacNumber = findScacNumberInList(firstFour, scacCodes);

  return {
    reference: remainder,
    selectedType: "mbol",
    typeManuallySet,
    selectedScacNumber: pastedScacNumber ?? SCAC_OTHER_VALUE,
    scacOtherCode: pastedScacNumber ? "" : firstFour,
  };
}

/**
 * Legacy MBOL paste assumed a combined value: 4-letter SCAC + 7-10 digit bill number,
 * stored as `SCAC||number`. When the user has manually chosen a type, that selection wins
 * over auto-detection. Container-shaped pastes only switch to container when type was not
 * manually overridden.
 */
export function resolvePastedReference(
  pastedValue: string,
  allowedTypeValues: string[],
  scacCodes: ScacCode[],
  selectedType: string,
  typeManuallySet: boolean,
): ResolvedPastedReference {
  const trimmed = pastedValue.trim();

  if (typeManuallySet) {
    if (selectedType === "mbol" && allowedTypeValues.includes("mbol")) {
      const mbolPaste = resolveMbolPasteSplit(trimmed, scacCodes, true);
      if (mbolPaste) return mbolPaste;

      return {
        reference: trimmed,
        selectedType: "mbol",
        typeManuallySet: true,
        selectedScacNumber: null,
        scacOtherCode: "",
      };
    }

    if (selectedType === "container_no") {
      const reference = trimmed.toUpperCase();

      return {
        reference,
        selectedType: "container_no",
        typeManuallySet: true,
        selectedScacNumber: resolveAutoContainerScacNumber(reference, scacCodes),
        scacOtherCode: "",
      };
    }

    return {
      reference: trimmed,
      selectedType,
      typeManuallySet: true,
      selectedScacNumber: null,
      scacOtherCode: "",
    };
  }

  if (detectTrackingType(trimmed, allowedTypeValues) === "container_no" && CONTAINER_PATTERN.test(trimmed)) {
    const reference = trimmed.toUpperCase();

    return {
      reference,
      selectedType: "container_no",
      typeManuallySet: false,
      selectedScacNumber: resolveAutoContainerScacNumber(reference, scacCodes),
      scacOtherCode: "",
    };
  }

  if (allowedTypeValues.includes("mbol")) {
    const mbolPaste = resolveMbolPasteSplit(trimmed, scacCodes, false);
    if (mbolPaste) return mbolPaste;
  }

  let reference = trimmed;
  const nextType = detectTrackingType(reference, allowedTypeValues);
  if (["rail_car", "container_no"].includes(nextType)) {
    reference = reference.toUpperCase();
  }

  return {
    reference,
    selectedType: nextType,
    typeManuallySet: false,
    selectedScacNumber: nextType === "container_no" ? resolveAutoContainerScacNumber(reference, scacCodes) : null,
    scacOtherCode: "",
  };
}
